import { clamp01, frac, smoothstep } from '../../shared/scalar';
import { BEATS_PER_BAR } from '../../shared/tempo';
import { hash } from '../hash';

const TAU = Math.PI * 2;

export interface Spot {
  readonly x: number;
  readonly y: number;
  readonly alpha: number;
}

// One flame on in a bar, so that a loop of the mask ends where the next begins.
export const FLAME = { count: 12, from: 37, length: 30, pulse: 4 };

export function flamePulse(beats: number): number {
  return Math.exp(-frac(beats) * 5);
}

export function sunTurn(loopShare: number): number {
  return (loopShare * TAU) / FLAME.count;
}

// Their eyes blink just after the one, never on the frame calm mode keeps.
export function blinking(beats: number): boolean {
  const into = ((beats % BEATS_PER_BAR) + BEATS_PER_BAR) % BEATS_PER_BAR;
  return into > 0 && into < 0.5;
}

export const GROWN = 1.08;
const OPEN_BEATS = 1;

export function opening(sinceBeats: number): number {
  return smoothstep(clamp01(sinceBeats / OPEN_BEATS));
}

export const AURA = { rings: 3, ringBeats: 2, wisps: 12, lobes: 9, wobble: 0.03, riseBeats: 2 };

export interface HeatRing {
  readonly share: number;
  readonly wobble: number;
  readonly phase: number;
  readonly alpha: number;
}

export function heatRing(index: number, beats: number): HeatRing {
  const p = frac(beats / AURA.ringBeats + index / AURA.rings);
  return {
    share: 0.33 + p * 0.66,
    wobble: AURA.wobble * (1 - p),
    phase: beats * 0.7 + index,
    alpha: 0.32 * Math.sin(p * Math.PI),
  };
}

export function wobblyPoint(
  step: number,
  steps: number,
  radius: number,
  wobble: number,
  phase: number,
): [number, number] {
  const angle = (step / steps) * TAU;
  const at = radius * (1 + wobble * Math.sin(AURA.lobes * angle + phase));
  return [Math.cos(angle) * at, Math.sin(angle) * at];
}

export interface Wisp extends Spot {
  readonly sway: number;
}

export function wisp(index: number, beats: number): Wisp {
  const angle = hash(index, 1) * TAU;
  const far = 0.25 + hash(index, 2) * 0.65;
  const rise = frac(beats / AURA.riseBeats + hash(index, 3));
  return {
    x: Math.cos(angle) * far,
    y: Math.sin(angle) * far - rise * 0.11,
    alpha: 0.16,
    sway: beats * 3,
  };
}

export const SMOKES = 3;

export function smoke(index: number, beats: number): Wisp {
  const rise = frac(beats + index / SMOKES);
  return { x: -0.5 + index * 0.5, y: -1.2 - rise * 1.3, alpha: 0.7 * (1 - rise), sway: beats * 4 };
}

export const COOL_DOTS = 12;

export function coolDot(index: number, beats: number): Spot {
  const rise = frac(beats * 0.7 + hash(index, 4));
  const angle = hash(index, 5) * TAU;
  return {
    x: Math.cos(angle) * rise,
    y: Math.sin(angle) * rise * 0.85 - rise * 0.23,
    alpha: 0.6 * (1 - rise),
  };
}

export const EXIT_BEATS = 4;

export interface Flight extends Spot {
  readonly turn: number;
}

export function exitFlame(index: number, p: number): Flight {
  const angle = (index / FLAME.count) * TAU;
  const far = 1 + p * 3.5;
  return { x: Math.cos(angle) * far, y: Math.sin(angle) * far, turn: angle + p * 4, alpha: 1 - p };
}

export function coolWave(p: number): { readonly radius: number; readonly alpha: number } {
  return { radius: 0.75 + p * 5, alpha: 0.3 * Math.sin(clamp01(p) * Math.PI) };
}
